// Cookie-based SSO auth. Reads the rw_token Cognito JWT from the shared
// .redwoodoperations.com cookie. On missing/expired token, redirects to
// auth.redwoodoperations.com for login.

import { useState, useEffect } from 'react';

interface AuthUser {
  sub: string;
  email: string;
  name: string;
  groups: string[];
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function parseJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 + pad to multiple of 4 (atob requires padding)
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    // Decode, handling UTF-8 (Cognito names may include non-ASCII)
    const binary = atob(b64);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(json);
  } catch (err) {
    console.error('JWT parse failed:', err);
    return null;
  }
}

function getUser(): AuthUser | null {
  const token = getCookie('rw_token');
  if (!token) return null;

  const payload = parseJwtPayload(token);
  if (!payload) return null;

  // Check expiry
  const exp = payload.exp as number;
  if (exp && exp * 1000 < Date.now()) return null;

  // Check mapsly access
  const groups = (payload['cognito:groups'] as string[]) ?? [];
  if (!groups.some((g) => g.startsWith('mapsly:'))) return null;

  return {
    sub: payload.sub as string,
    email: payload.email as string,
    name: (payload.name as string) ?? (payload.email as string),
    groups,
  };
}

export function isAdmin(user: AuthUser): boolean {
  return user.groups.includes('mapsly:admin');
}

function redirectToLogin(): void {
  const redirect = encodeURIComponent(window.location.href);
  window.location.href = `https://auth.redwoodoperations.com?redirect=${redirect}`;
}

export function useAuth(): { user: AuthUser | null; loading: boolean } {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const u = getUser();
    if (!u) {
      redirectToLogin();
      return;
    }
    setUser(u);
    setLoading(false);
  }, []);

  return { user, loading };
}

export function getToken(): string | null {
  return getCookie('rw_token');
}
